import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useStats } from '../contexts/StatsContext';
import { mockData } from '../data/mock';
import { Button } from '../components/ui/button';
import { Star, ArrowRight, ChevronDown } from 'lucide-react';

const HeroSection = () => {
  const { t, language } = useLanguage();
  const { googleReviews, websitePrice } = useStats();

  // Enhanced Google Ads Conversion + GA4 Tracking
  const handleBookingClick = () => {
    try {
      const consentStatus = localStorage.getItem('cookieConsent');
      const hasConsent = consentStatus === 'all' || consentStatus === 'accepted';
      
      if (typeof window.gtag !== 'function' || !hasConsent) return;

      // Check if conversion already fired this session (double-click protection)
      const conversionFired = sessionStorage.getItem('booking_conversion_fired');
      
      if (!conversionFired) {
        // Fire Google Ads conversion (once per session)
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17076406474/1470Cln77dsaEOLnvs4'
        });
        sessionStorage.setItem('booking_conversion_fired', 'true');
      }

      // Fire GA4 analytics event (every click)
      window.gtag('event', 'booking_click', {
        'event_category': 'engagement',
        'event_label': 'booking_cta',
        'value': 1
      });
    } catch {
      // Fail silently
    }
  };

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden bg-neutral-900">
      {/* Mobile: Full-screen visual impact */}
      <div className="lg:hidden relative flex-1 flex flex-col">
        {/* Animated Background */}
        <div className="absolute inset-0">
          {/* Red gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-red-600/20 via-neutral-900/80 to-neutral-900 z-10" />
          
          {/* Animated circles */}
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px]">
            <div className="absolute inset-0 border-2 border-red-500/20 rounded-full animate-ping-slow" />
            <div className="absolute inset-8 border-2 border-red-500/30 rounded-full animate-ping-slower" />
            <div className="absolute inset-16 border-2 border-red-500/40 rounded-full animate-ping-slowest" />
          </div>
        </div>

        {/* Content */}
        <div className="relative z-20 flex-1 flex flex-col pt-20">
          {/* Top: Large animated First Aid Symbol */}
          <div className="flex-1 flex items-center justify-center px-6 py-8">
            <div className="relative">
              {/* Pulsing glow */}
              <div className="absolute inset-0 bg-red-600 rounded-full blur-3xl opacity-30 animate-pulse-glow" />
              
              {/* Main Symbol Container */}
              <div className="relative w-56 h-56 sm:w-72 sm:h-72">
                {/* Outer ring - targeting reticle */}
                <svg 
                  viewBox="0 0 200 200" 
                  className="absolute inset-0 w-full h-full animate-spin-very-slow"
                >
                  <circle cx="100" cy="100" r="95" fill="none" stroke="#dc2626" strokeWidth="2" opacity="0.6" />
                  {/* Reticle marks */}
                  <line x1="100" y1="0" x2="100" y2="20" stroke="#dc2626" strokeWidth="3" />
                  <line x1="100" y1="180" x2="100" y2="200" stroke="#dc2626" strokeWidth="3" />
                  <line x1="0" y1="100" x2="20" y2="100" stroke="#dc2626" strokeWidth="3" />
                  <line x1="180" y1="100" x2="200" y2="100" stroke="#dc2626" strokeWidth="3" />
                </svg>
                
                {/* Inner pulsing circle */}
                <div className="absolute inset-8 rounded-full bg-red-600/10 animate-pulse-ring" />
                
                {/* First Aid Cross with heartbeat */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    {/* Cross shape */}
                    <div className="relative w-32 h-32 sm:w-40 sm:h-40 animate-heartbeat">
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 sm:w-14 h-32 sm:h-40 bg-red-600 rounded-lg shadow-2xl shadow-red-600/50" />
                      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-32 sm:w-40 h-12 sm:h-14 bg-red-600 rounded-lg shadow-2xl shadow-red-600/50" />
                      
                      {/* Heartbeat line */}
                      <svg 
                        viewBox="0 0 120 40" 
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 sm:w-28 h-10"
                      >
                        <path 
                          d="M0,20 L30,20 L40,5 L50,35 L60,10 L70,25 L80,20 L120,20" 
                          fill="none" 
                          stroke="white" 
                          strokeWidth="3" 
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="animate-heartbeat-line"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* FOKUS text below symbol */}
              <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 text-center">
                <p className="text-xs tracking-[0.3em] text-red-400 font-medium mb-1">ERSTE-HILFE</p>
                <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight">FOKUS</h1>
              </div>
            </div>
          </div>

          {/* Bottom: Key info and CTA */}
          <div className="relative z-20 px-6 pb-8 pt-20">
            {/* Location badge */}
            <div className="flex justify-center mb-4">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <span className="text-white/90 text-sm font-medium">Düsseldorf</span>
              </div>
            </div>
            
            {/* Rating */}
            <div className="flex justify-center items-center gap-2 mb-6">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="text-white/70 text-sm"><span className="api-google-reviews">{googleReviews}</span>+ Bewertungen</span>
            </div>

            {/* CTA Button */}
            <a
              href={mockData.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleBookingClick}
              className="block mb-4"
              data-testid="hero-mobile-booking-btn"
            >
              <Button 
                size="lg" 
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-5 text-lg rounded-2xl shadow-xl shadow-red-600/40"
              >
                <span>Jetzt Kurs buchen</span>
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            
            {/* Price */}
            <p className="text-center text-white/60 text-sm">
              Ab <span className="text-white font-bold text-lg api-website-price">{websitePrice}€</span> • Mo-So
            </p>
            
            {/* Scroll indicator */}
            <div className="flex justify-center mt-6">
              <ChevronDown className="w-6 h-6 text-white/40 animate-bounce" />
            </div>
          </div>
        </div>
      </div>

      {/* Desktop: Original layout */}
      <div className="hidden lg:flex relative min-h-screen items-center bg-gradient-hero">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 -left-20 w-96 h-96 bg-red-100 rounded-full blur-3xl opacity-60" />
          <div className="absolute bottom-40 -right-20 w-80 h-80 bg-red-50 rounded-full blur-3xl opacity-40" />
        </div>

        <div className="container-custom relative z-10 py-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              {/* Main Heading */}
              <div>
                <h1 className="text-5xl xl:text-7xl font-extrabold text-neutral-900 leading-[1.05] tracking-tight">
                  {t('hero.title')}{' '}
                  <span className="text-gradient">{t('hero.titleHighlight')}</span>
                </h1>
                <p className="mt-6 text-xl lg:text-2xl text-neutral-600 font-medium max-w-xl">
                  {t('hero.subtitle')}
                </p>
              </div>

              {/* Quick Benefits */}
              <div className="flex flex-wrap gap-4">
                {t('quickInfo.items').slice(0, 3).map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2.5 text-neutral-700 bg-white/60 backdrop-blur-sm px-4 py-2.5 rounded-xl border border-neutral-100/50"
                  >
                    <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm font-medium">{item}</span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={mockData.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleBookingClick}
                  data-testid="hero-desktop-booking-btn"
                >
                  <Button 
                    size="lg" 
                    className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-6 text-lg rounded-2xl shadow-xl shadow-red-600/30 hover:shadow-2xl hover:shadow-red-600/40 transition-all duration-300 group"
                  >
                    <span>{t('hero.cta')}</span>
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
                <a href="/#/bg-kurse" data-testid="hero-bg-courses-btn">
                  <Button 
                    variant="outline" 
                    size="lg"
                    className="border-2 border-neutral-300 text-neutral-700 hover:border-red-600 hover:text-red-600 hover:bg-red-50 font-semibold px-8 py-6 text-lg rounded-2xl transition-all duration-300"
                  >
                    {t('hero.bgCta')}
                  </Button>
                </a>
              </div>

              {/* Price Tag */}
              <div className="inline-flex items-center gap-3 bg-white px-5 py-3 rounded-2xl shadow-lg border border-neutral-100">
                <span className="text-3xl font-extrabold text-red-600 api-website-price">{websitePrice}€</span>
                <span className="text-sm text-neutral-500">inkl. 19% MwSt.</span>
              </div>
            </div>

            {/* Right Content - Hero Image */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={mockData.images.hero}
                  alt="Erste-Hilfe Kurs in Düsseldorf"
                  className="w-full h-auto object-cover aspect-[4/3]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="glass rounded-2xl p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-neutral-600 font-medium">
                          {t('courseTimes.day')}
                        </p>
                        <p className="text-lg font-bold text-neutral-900">
                          {t('courseTimes.time')}
                        </p>
                      </div>
                      <div className="bg-red-600 text-white px-5 py-2.5 rounded-xl font-bold text-xl shadow-lg shadow-red-600/30 api-website-price">
                        {websitePrice}€
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Stats Badge */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl p-5 animate-float border border-neutral-100">
                <div className="text-center">
                  <p className="text-3xl font-extrabold text-red-600 api-google-reviews">{googleReviews}+</p>
                  <p className="text-xs text-neutral-500 font-medium">5-Sterne Bewertungen</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wave Divider - Desktop only */}
      <div className="hidden lg:block absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
          <path
            d="M0 100L48 90C96 80 192 60 288 50C384 40 480 40 576 45C672 50 768 60 864 65C960 70 1056 70 1152 65C1248 60 1344 50 1392 45L1440 40V100H1392C1344 100 1248 100 1152 100C1056 100 960 100 864 100C768 100 672 100 576 100C480 100 384 100 288 100C192 100 96 100 48 100H0Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
